<template>
  <ul class="list-none flex gap-0.5 flex-1 justify-end">
    <li v-show="canOrderPrint" class="flex items-center">
      <u-tooltip text="Print">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="COMMON_ICONS.print"
          @click="orderPrints"
        />
      </u-tooltip>
    </li>
    <li v-show="file.is.project" class="flex items-center">
      <u-tooltip text="Add to Cart">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="COMMON_ICONS.print"
          @click="addToCart"
        />
      </u-tooltip>
    </li>

    <li v-show="file.is.editableJupiterProject">
      <u-tooltip text="Edit">
        <core-selection-toolbar-button
          dark
          size="lg"
          icon="i-ri-edit-fill"
          @click="editJupiterProject"
        />
      </u-tooltip>
    </li>
    <li v-show="file.is.sharable">
      <u-tooltip text="Share">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="COMMON_ICONS.share"
          @click="shareFile"
        />
      </u-tooltip>
    </li>

    <li v-show="isZoomable && !file.is.errored" class="hidden sm:block">
      <u-tooltip text="Zoom">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="isZoomed ? 'i-ri-zoom-out-line' : 'i-ri-zoom-in-line'"
          @click="zoom()"
        />
      </u-tooltip>
    </li>

    <li v-show="file.is.ownedByCurrentUser">
      <u-tooltip text="Favorite">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="file.favorite ? COMMON_ICONS.favoriteFill : COMMON_ICONS.favoriteLine"
          @click="toggleFavorite"
        />
      </u-tooltip>
    </li>

    <li v-show="file.is.copyableFromUser && file.user">
      <u-tooltip text="Copy">
        <core-selection-toolbar-button
          dark
          size="lg"
          icon="i-ri-file-copy-2-fill"
          @click="copyFileFromUser"
        />
      </u-tooltip>
    </li>

    <li v-if="file.is.video && file.is.ownedByCurrentUser">
      <u-dropdown
        class="h-full"
        :items="videoToolsItems"
        :open="isVideoToolsOpen"
        @update:open="updateVideoToolsOpen"
      >
        <u-tooltip text="Video Tools" :prevent="isVideoToolsOpen">
          <core-selection-toolbar-button
            premium
            :premium-feature-unlocked="hasVideoPlan"
            dark
            size="lg"
            icon="i-ri-play-large-line"
            @click.stop.prevent="openVideoTools"
          />
        </u-tooltip>
      </u-dropdown>
    </li>

    <li>
      <u-tooltip :text="isPanelVisible ? 'Hide Info' : 'Show Info'">
        <core-selection-toolbar-button
          dark
          size="lg"
          icon="i-ri-information-2-line"
          @click="emit('toggle-panel', true)"
        />
      </u-tooltip>
    </li>

    <li v-show="userContext.canUserDelete.value">
      <u-tooltip text="Delete">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="COMMON_ICONS.delete"
          @click="deleteFile"
        />
      </u-tooltip>
    </li>

    <li v-if="overflowItems[0].length">
      <u-dropdown class="h-full" :items="overflowItems">
        <core-selection-toolbar-button
          dark
          size="lg"
          :icon="COMMON_ICONS.moreVertical"
        />
      </u-dropdown>
    </li>
  </ul>
</template>

<script setup>
    import {storeToRefs} from 'pinia';
    import {VideoEditor} from '#components';

    const emit = defineEmits(['toggle-panel', 'download-file', 'removed'])
    const props = defineProps({
      isPanelVisible: Boolean,
      isDownloadPending: Boolean
    });

    const fileContext = inject('fileContext');

    const filesStore = useFilesStore();
    const {file, files} = storeToRefs(filesStore);
    const fileActions = useFileActions();
    const projectActions = useProjectActions();

    const sessionStore = useSessionStore();
    const {currentUser} = storeToRefs(sessionStore);

    const setDefaultZoomEvent = useEventBus('file-detail-set-default-zoom');
    const updateZoomEvent = useEventBus('file-detail-update-zoom');
    const captureVideoFrameEvent = useEventBus('file-detail-capture-video-frame');

    const userContext = useUserContext({entity: file});
    const {openStorageUpsellModal, openVideoUpsellModal, openVideoEditWelcomeModal} = useCommonModals();

    const hasVideoPlan = computed(() => Boolean(currentUser.value?.can?.transcodeVideo));
    const canUseCurrentVideoTools = computed(() =>
      hasVideoPlan.value &&
      file.value.is.ownedByCurrentUser &&
      file.value.is.video &&
      !file.value.is.errored
    );
    const canChooseThumbnail = computed(() => canUseCurrentVideoTools.value && file.value.screen_caps_count > 1);
    const canOrderPrint = computed(() => file.value.is.printablePhoto && userContext.isEntityOwner.value);
    const isZoomable = computed(() => !(file.value.is.video || file.value.is.audio));
    const isRotatable = computed(() => file.value.is.editablePhoto && !file.value.is.gif);
    const {isPlayable: isPlayableVideo} = useVideoState({file});
    const isVideoToolsOpen = ref(false);

    const videoToolsItems = computed(() => [[
      {
        label: 'Edit Video',
        icon: 'i-ri-edit-line',
        click: openVideoEditor,
        disabled: !(canUseCurrentVideoTools.value && isPlayableVideo.value)
      },
      {
        label: 'Capture Frame',
        icon: 'i-ri-camera-line',
        click: captureVideoFrame,
        disabled: !(canUseCurrentVideoTools.value && isPlayableVideo.value)
      },
      canChooseThumbnail.value && {
        label: 'Change Preview',
        icon: 'i-ri-image-fill',
        click: setThumbnail
      }
    ].filter(Boolean)]);

    const overflowItems = computed(() => [
      file.value.is.editablePhoto && [

        {
          label: 'Edit Photo',
          icon: 'i-ri-crop-2-fill',
          click: editPhoto
        },

        {
          label: 'Rotate Left',
          icon: 'i-ri-anticlockwise-2-line',
          click: rotate
        },

        {
          label: 'Rotate Right',
          icon: 'i-ri-clockwise-2-line',
          click: () => rotate({clockwise: true})
        }
      ].filter(Boolean),
      [
        {
          label: 'Download',
          icon: COMMON_ICONS.download,
          click: () => emit('download-file'),
          disabled: props.isDownloadPending
        }
      ].filter(Boolean)
    ].filter(Boolean));

    const isZoomed = ref(false);

    function addToCart() {
      projectActions.addProjectToCart({project: file.value, ownerId: file.value.user.id});
    }

    async function editPhoto() {
      await useFileActions().editImage({
        file: file.value,
        contextType: fileContext.contextType,
        contextId: fileContext.contextId
      });
    }

    async function editJupiterProject() {
      let editProject = file.value;

      if (file.value.is.purchased) {
        const {projectCopy} = await projectActions.copyProject({project: editProject, isEditAsCopy: true});

        if (projectCopy) {
          useFilesStore().prependFiles({files: [projectCopy]});

          editProject = projectCopy;
        }
      }

      navigateTo(`/app/print-designer/${editProject.provider_uid}`, {external: true});
    }

    async function orderPrints() {
      projectActions.openFilePrintModal({file: file.value});
    }

    function toggleFavorite() {
      useFileActions().toggleFavorite({file: file.value});
    }

    function shareFile() {
      fileActions.share({files: file.value});
    }

    function captureVideoFrame() {
      useGtm().trackEvent({ event: 'core_ui-capture_frame-click', action: 'click', category: 'video-editor' });
      captureVideoFrameEvent.emit();
    }

    async function openVideoTools() {
      if (isVideoToolsOpen.value) {
        isVideoToolsOpen.value = false;
        return;
      }

      await sessionStore.getCurrentUser();
      const user = sessionStore.currentUser;

      if (!user) return;

      if (!user.can?.transcodeVideo) {
        openVideoUpsellModal({
          context: VIDEO_UPSELL_CONTEXTS.videoTools
        });
        return;
      }

      if (user.isStorageFull) {
        openStorageUpsellModal({
          context: STORAGE_UPSELL_CONTEXTS.videoToolsStorageFull
        });
        return;
      }

      isVideoToolsOpen.value = true;
    }

    function updateVideoToolsOpen(isOpen) {
      isVideoToolsOpen.value = isOpen;
    }

    async function openVideoEditor() {
      useGtm().trackEvent({ event: 'core_ui-video_edit-click', action: 'click', category: 'video-editor' });

      if (useDevice().isMobileOrTablet) {
        const mobileModal = useConfirmModal({
          id: 'video-editor-mobile-no',
          heading: 'Larger Screen Required',
          message: `The Video Editor isn't available on smaller screens yet. Please switch to a desktop or laptop to use this tool.`,
          confirmText: 'Ok',
          nonCancelable: true,
          confirmMethod: () => mobileModal.close()
        });
        mobileModal.open();
        return;
      }

      useEventBus('core-media-player-stop-playback').emit();

      if (
        !sessionStore.isImpersonating &&
        !usePreferencesStore().currentUserPreferences[CURRENT_USER_PREFERENCES.onbVideoEditDismissed]
      ) {
        await openVideoEditWelcomeModal();
      }

      useEventBus('core-media-player-stop-playback').emit();

      useFModal({
        id: 'video-editor',
        size: 'fullscreen',
        clickToClose: false,
        showClose: false,
        escToClose: false,
        component: VideoEditor,
        props: {
          file: file.value,
          fileContext
        }
      }).open();
    }

    function zoom() {
      setDefaultZoomEvent.emit({file: file.value, action: isZoomed.value ? 'zoom-out' : 'zoom-in'})
    }

    function updateZoomState({isZoomed: updatedZoomState}) {
      isZoomed.value = updatedZoomState;
    }

    async function rotate({clockwise} = {}) {
      try {
        const rotationDelta = clockwise ? 90 : -90;
        const updates = {rotation: (file.value.images?.rotation ?? 0) + rotationDelta};
        await filesStore.updateFile({id: file.value.id, updates});
      } catch (e) {
        useErrorToast().add();
      }
    }

    async function setThumbnail() {
      await fileActions.setVideoThumbnail({file: file.value});
      //re-fetch the file
      await filesStore.getFileDetails({files: [file.value], refresh: true, ...fileContext});
    }

    function copyFileFromUser() {
      fileActions.copyFilesToCurrentUserAccount({files: file.value, userId: fileContext.userId});
    }

    async function deleteFile() {
      const fileIndexInStore = Array.isArray(files.value) ? files.value.findIndex(sf => sf.id === file.value.id) : -1;
      await fileActions.deleteFiles({files: file.value});
      emit('removed', {fileIndexInStore});
    }

    onMounted(() => {
      updateZoomEvent.on(updateZoomState);
    });

    onBeforeUnmount(() => {
      updateZoomEvent.off(updateZoomState);
    });

    watch(file, async () => {
      isZoomed.value = false;
      isVideoToolsOpen.value = false;
    });

</script>
